import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Colors, CreditDetails } from 'Types';
import {
	clx,
	CommunityPartnerCreditStatus,
	currencyFormatter,
	formatLocaleDateTime
} from 'Utils';
import { Card, Badge } from 'Elements';
import styles from 'partials/card/subscription-card.module.scss';
import { FC } from 'react';
import { SkeletonCredit } from '../skeleton/SkeletonCredit';

interface Props {
	credit: CreditDetails | undefined;
	isLoading: boolean;
}

export const Credit: FC<Props> = ({ credit, isLoading }) => {
	const { t } = useTranslation();
	const getStatusData = (status) => {
		return CommunityPartnerCreditStatus.find(
			(elem) => elem.value === status
		);
	};

	return isLoading ? (
		<SkeletonCredit />
	) : (
		<Col>
			<Card className="h-100">
				<h6
					className={clx(
						styles.row,
						'd-flex align-items-center justify-content-between text-gray-3 fs-6 fw-400'
					)}>
					<span className={styles.rowLabel}>{t('title.credit')}</span>
					<div className="d-flex align-items-center gap-2">
						<Badge
							color={
								getStatusData(credit?.creditStatus)?.color ??
								Colors.primary
							}>
							{t(`${getStatusData(credit?.creditStatus)?.label}`)}
						</Badge>
						{credit?.donated ? (
							<Badge color={Colors.association}>
								{t('badge.donated')}
							</Badge>
						) : (
							<></>
						)}
					</div>
				</h6>
				<Row>
					<Col sm={12}>
						<div
							className={clx(
								styles.row,
								'd-flex align-items-center justify-content-between'
							)}>
							<span className={styles.rowLabel}>
								{t('label.credit-number')}
							</span>
							<span className="fs-6">
								{credit?.creditNumber ?? '---'}
							</span>
						</div>
						<div
							className={clx(
								styles.row,
								'd-flex align-items-center justify-content-between'
							)}>
							<span className={styles.rowLabel}>
								{t('label.current-active-abo')}
							</span>
							<span>{credit?.activeAboCount ?? '---'}</span>
						</div>
						<div
							className={clx(
								styles.row,
								'd-flex align-items-center justify-content-between'
							)}>
							<span className={styles.rowLabel}>
								{t('label.created-at')}
							</span>
							<span>
								{credit?.createdAt
									? formatLocaleDateTime(credit?.createdAt)
									: '---'}
							</span>
						</div>
						{credit?.taxAmount && credit?.taxAmount !== 0 ? (
							<div
								className={clx(
									styles.row,
									'd-flex align-items-center justify-content-between'
								)}>
								<span className={styles.rowLabel}>
									{t('label.tax')}
									<span className="ms-1 text-gray-3 fs-small">
										({credit?.taxPercent ?? 0}%)
									</span>
								</span>
								<span>
									{currencyFormatter(
										credit?.taxAmount &&
											credit.taxAmount !== 0
											? credit.taxAmount
											: 0
									)}
								</span>
							</div>
						) : (
							<></>
						)}
						<div
							className={clx(
								styles.row,
								'd-flex align-items-center justify-content-between'
							)}>
							<span className={clx(styles.rowLabel, 'fw-500')}>
								{t('label.total')}
							</span>
							<div className="d-flex align-items-center text-end">
								<span className="fs-6 fw-500">
									{currencyFormatter(
										credit?.totalCreditAmount ?? 0
									)}
								</span>
								<span className="text-gray-3 ms-2 fs-small">
									(
									{credit?.includedTax
										? t('label.tax-included')
										: t('label.no-tax')}
									)
								</span>
							</div>
						</div>
					</Col>
				</Row>
			</Card>
		</Col>
	);
};
